// ============================================================
// Control360 — Llamadas IA (Lucy)
// Vista: resumen del mes + lista de llamadas, cierres resaltados
// en verde con acceso directo a "Crear orden" (mismo patrón que
// Telemercadeo → NuevaOrden.js, usando sessionStorage).
//
// ✅ FIX LUCY-ELEVEN-004 (2026-07-19) — controles de operación
// (pedido de Sandra, mismo patrón que Anny):
//   · "Lanzar ahora": corre el motor SOLO para este tenant, ya.
//   · "Programar": elige día y hora (hora Colombia) — el backend
//     ejecuta la corrida automáticamente a esa hora.
//   · "Llamada de prueba": Lucy llama a un número puntual con
//     datos de ejemplo (validar guion/voz sin clientes reales).
//   · Barra de consumo: minutos usados vs tope del mes.
// Solo visible para rol admin (los sub-usuarios ven la lista).
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const RESULTADOS = {
  cerrada:           { label: 'Cierre confirmado', bg: '#dcfce7', color: '#15803d', icon: '✅' },
  reagendada:        { label: 'Reagendada',         bg: '#e0f2fe', color: '#0369a1', icon: '📅' },
  inactivo_cliente:  { label: 'Cliente inactivo',   bg: '#f3f4f6', color: '#6b7280', icon: '🚫' },
  escalado_asesor:   { label: 'Escalado a asesor',  bg: '#fff8e6', color: '#b45309', icon: '👤' },
  no_interesado:     { label: 'No interesado',      bg: '#fee2e2', color: '#b91c1c', icon: '✕' },
  sin_respuesta:     { label: 'No contestó',        bg: '#f3f4f6', color: '#6b7280', icon: '📵' },
};
const EN_CURSO = { label: 'En curso', bg: '#ede9fe', color: '#7c3aed', icon: '☎' };

const telBonito = (t) => {
  if (!t) return '';
  const s = String(t).replace(/^57/, '');
  return s.length === 10 ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}` : s;
};

const formatFecha = (d) => {
  if (!d?.seconds) return '';
  const f = new Date(d.seconds * 1000);
  return f.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ✅ LUCY-MES-004 — helpers de mes en hora Colombia (UTC-5), mismo criterio
// que el backend: el mes se decide por la hora de Colombia, no la del navegador.
const mesActualCO = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 7);

const NOMBRE_MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const mesBonito = (mes) => {
  if (!/^\d{4}-\d{2}$/.test(String(mes || ''))) return '';
  const [a, m] = mes.split('-');
  return `${NOMBRE_MES[Number(m) - 1]} ${a}`;
};

// Lista de meses seleccionables: 6 atrás (recuperar lo que quedó pendiente)
// y 1 adelante (adelantar la corrida del mes entrante).
const mesesSeleccionables = () => {
  const base = new Date(Date.now() - 5 * 3600 * 1000);
  const out = [];
  for (let i = -6; i <= 1; i++) {
    const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + i, 1));
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
};

const OPCIONES_PAQUETE = [
  { valor: '25',  etiqueta: '25 llamadas' },
  { valor: '50',  etiqueta: '50 llamadas' },
  { valor: '100', etiqueta: '100 llamadas' },
  { valor: '200', etiqueta: '200 llamadas' },
  { valor: '',    etiqueta: 'Todas (sin tope)' },
];

export default function LlamadasIA({ user, onNavegar }) {
  const [lista, setLista] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [activa, setActiva] = useState(null);
  const [config, setConfig] = useState(null); // { topeMinutosMes, minutosConsumidosMes }
  const [filtroResultado, setFiltroResultado] = useState('');
  const [detalle, setDetalle] = useState(null);

  // FIX LUCY-ELEVEN-004: estado de los controles de operación
  const [programadas, setProgramadas] = useState([]);
  const [modalProgramar, setModalProgramar] = useState(false);
  const [modalPrueba, setModalPrueba] = useState(false);
  const [progFecha, setProgFecha] = useState('');
  const [progHora, setProgHora] = useState('09:00');
  const [telPrueba, setTelPrueba] = useState('');
  const [tipoPrueba, setTipoPrueba] = useState('empresa'); // 'vehicular' | 'empresa'
  const [preorden, setPreorden] = useState(null); // LUCY-PREORDEN-001
  const [corrida, setCorrida] = useState(null);   // LUCY-ASINCRONO-001
  // ✅ LUCY-MES-004 / LUCY-PAQUETE-005: qué mes se llama y de a cuántos.
  // Por defecto el mes actual y un paquete de 50 — con minutos limitados es
  // preferible cerrar bien 50 que arrancar 600 y quedarse a mitad de camino.
  const [mesCorrida, setMesCorrida] = useState(mesActualCO());
  const [paquete, setPaquete] = useState('50');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null); // { tipo: 'ok'|'error', texto }

  const esAdmin = user?.role === 'admin' || user?.superAdmin;

  const mostrarAviso = (tipo, texto) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        fetch(`${API}/llamadas-ia/resumen`, { headers: authHeaders() }),
        fetch(`${API}/llamadas-ia`, { headers: authHeaders() }),
        fetch(`${API}/llamadas-ia/config`, { headers: authHeaders() }),
        fetch(`${API}/llamadas-ia/programadas`, { headers: authHeaders() }),
        fetch(`${API}/llamadas-ia/corrida`, { headers: authHeaders() }),
      ]);
      const [res, lst, cfg, prog, cor] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json(), r5.json()]);
      setResumen(res);
      setLista(Array.isArray(lst) ? lst : []);
      setActiva(!!cfg?.activo);
      setConfig(cfg);
      setProgramadas(Array.isArray(prog) ? prog.filter(p => p.estado === 'pendiente') : []);
      setCorrida(cor && cor.estado !== 'ninguna' ? cor : null);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ✅ LUCY-ASINCRONO-001: mientras Lucy esté llamando, la pantalla se refresca
  // sola cada 10 s. Así se ve a quién está llamando en este momento sin que
  // nadie tenga que recargar. Cuando la corrida termina, el intervalo se corta
  // solo — no queda consultando la API para siempre.
  useEffect(() => {
    if (corrida?.estado !== 'en_curso') return;
    const t = setInterval(() => { cargar(); }, 10000);
    return () => clearInterval(t);
  }, [corrida?.estado, cargar]);

  // ─── Acciones de operación (FIX LUCY-ELEVEN-004) ───────────────────────────
  const lanzarAhora = async () => {
    // ✅ LUCY-MES-004 / LUCY-PAQUETE-005: la confirmación dice EXACTAMENTE qué
    // se va a hacer. Antes decía "este mes" sin que se pudiera elegir otro.
    const cuantas = paquete ? `las primeras ${paquete} llamadas` : 'TODAS las llamadas pendientes';
    if (!window.confirm(`¿Lanzar ${cuantas} de los vencimientos de ${mesBonito(mesCorrida)}?\n\nLucy empieza por los clientes que aún no han recibido ninguna llamada, del vencimiento más próximo al más lejano.`)) return;
    setOcupado(true);
    try {
      const r = await fetch(`${API}/llamadas-ia/ejecutar-motor`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ mes: mesCorrida, maxLlamadas: paquete ? Number(paquete) : null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al lanzar el motor');

      // ✅ LUCY-ASINCRONO-001: el backend responde 202 apenas arranca; el motor
      // sigue llamando en segundo plano. El avance llega por el panel en vivo.
      if (data.iniciado) {
        mostrarAviso('ok', data.mensaje || 'Lucy empezó a llamar.');
        cargar();
        setOcupado(false);
        return;
      }
      // ✅ FIX LUCY-DIAGNOSTICO-001: el motor atrapa sus propios errores y
      // responde 200 con { error } — antes eso se veía como "0 llamadas" sin
      // explicación. Ahora el motivo real llega a pantalla.
      if (data.error) throw new Error(data.error);

      // ✅ FIX LUCY-DIAGNOSTICO-002: el backend ahora devuelve el desglose de
      // motivos de omisión. Antes, si el motor se saltaba media base, en
      // pantalla solo aparecía "0 llamadas" sin explicación.
      const m = data.motivos || {};
      const detalles = [
        data.omitidasPorTope   ? `${data.omitidasPorTope} por tope de minutos` : null,
        m.sin_telefono         ? `${m.sin_telefono} sin teléfono válido`       : null,
        m.ya_gestionado        ? `${m.ya_gestionado} ya gestionado(s)`         : null,
        m.intentos_agotados    ? `${m.intentos_agotados} con intentos agotados`: null,
        m.cliente_inexistente  ? `${m.cliente_inexistente} sin ficha de cliente` : null,
        m.fuera_de_horario     ? `${m.fuera_de_horario} fuera de horario`      : null,
        m.fallo_proveedor      ? `${m.fallo_proveedor} rechazada(s) por el proveedor` : null,
        m.sin_sede             ? `${m.sin_sede} sin sede con dirección (asigna empresa al cliente o marca una sede principal en Mi Empresa)` : null,
      ].filter(Boolean).join(' · ');

      if (!data.llamadasLanzadas) {
        mostrarAviso('error', detalles
          ? `No se lanzó ninguna llamada. Motivos: ${detalles}.`
          : 'No se lanzó ninguna llamada. Revisa que haya vencimientos de ESTE mes sin gestionar, con cliente y teléfono válido.');
      } else {
        mostrarAviso('ok',
          `Motor ejecutado: ${data.llamadasLanzadas} llamada(s) lanzada(s) de ${data.vencimientosEvaluados ?? '—'} vencimiento(s) evaluado(s)` +
          (detalles ? ` · Omitidas: ${detalles}` : ''));
      }
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
    setOcupado(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ LUCY-PARADA-001 — Freno de la corrida
  // El backend no mata el proceso: deja una señal que el motor lee antes de
  // cada llamada. Por eso el texto dice "se detendrá en segundos" — puede haber
  // una llamada ya marcada en vuelo y esa no se puede deshacer.
  // ═══════════════════════════════════════════════════════════════════════════
  const detenerCorrida = async (modo) => {
    const confirmacion = modo === 'cancelar'
      ? '¿CANCELAR la corrida? Las llamadas pendientes se descartan y tendrás que lanzarla de nuevo desde cero.'
      : '¿Pausar la corrida? Los pendientes quedan guardados y puedes continuar después justo donde quedó.';
    if (!window.confirm(confirmacion)) return;
    setOcupado(true);
    try {
      const r = await fetch(`${API}/llamadas-ia/corrida/detener`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ modo }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'No se pudo detener la corrida');
      mostrarAviso('ok', data.mensaje || 'Corrida detenida.');
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
    setOcupado(false);
  };

  const reanudarCorrida = async () => {
    setOcupado(true);
    try {
      const r = await fetch(`${API}/llamadas-ia/corrida/reanudar`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'No se pudo continuar la corrida');
      mostrarAviso('ok', data.mensaje || 'Lucy retomó la corrida.');
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
    setOcupado(false);
  };

  const programarCorrida = async () => {
    if (!progFecha || !progHora) { mostrarAviso('error', 'Elige fecha y hora'); return; }
    setOcupado(true);
    try {
      const r = await fetch(`${API}/llamadas-ia/programar`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          fecha: progFecha, hora: progHora,
          mes: mesCorrida,                                  // ✅ LUCY-MES-004
          maxLlamadas: paquete ? Number(paquete) : null,    // ✅ LUCY-PAQUETE-005
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al programar');
      mostrarAviso('ok', `Corrida de ${mesBonito(mesCorrida)}${paquete ? ` (paquete de ${paquete})` : ''} programada para el ${progFecha} a las ${progHora} (hora Colombia)`);
      setModalProgramar(false);
      setProgFecha('');
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
    setOcupado(false);
  };

  const cancelarProgramada = async (id) => {
    if (!window.confirm('¿Cancelar esta corrida programada?')) return;
    try {
      const r = await fetch(`${API}/llamadas-ia/programadas/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al cancelar');
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
  };

  const lanzarPrueba = async () => {
    const tel = telPrueba.replace(/\D/g, '');
    if (tel.length !== 10 || !tel.startsWith('3')) { mostrarAviso('error', 'Escribe un celular colombiano de 10 dígitos'); return; }
    setOcupado(true);
    try {
      const r = await fetch(`${API}/llamadas-ia/llamada-prueba`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ telefono: tel, tipoUso: tipoPrueba }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al lanzar la prueba');
      mostrarAviso('ok', data.mensaje || 'Llamada de prueba lanzada — tu teléfono sonará en unos segundos');
      setModalPrueba(false);
      setTelPrueba('');
      cargar();
    } catch (e) { mostrarAviso('error', e.message); }
    setOcupado(false);
  };

  const listaFiltrada = filtroResultado
    ? lista.filter(l => l.resultado === filtroResultado)
    : lista;

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ PREORDEN — LUCY-PREORDEN-001 (2026-07-26)
  // ───────────────────────────────────────────────────────────────────────────
  // ANTES: "Crear orden" saltaba directo a NuevaOrden con los datos crudos que
  // Lucy había recogido por teléfono. Si la transcripción entendía mal una
  // dirección o un NIT, el error entraba silencioso a la orden.
  //
  // AHORA: se abre una PREORDEN de revisión — los datos son editables, los
  // campos que Lucy no logró confirmar salen resaltados, y solo al confirmar
  // se pasa a NuevaOrden. Un dato dictado por voz siempre pasa por ojo humano.
  //
  // Los datos corregidos aquí NO se pierden: viajan en el prefill.
  // ═══════════════════════════════════════════════════════════════════════════
  const CAMPOS_PREORDEN = [
    { k: 'nombreCompleto', label: 'Nombre / Razón social', requerido: true },
    { k: 'nit',            label: 'Cédula o NIT',          requerido: true },
    { k: 'celular',        label: 'Celular',               requerido: true },
    { k: 'direccion',      label: 'Dirección',             requerido: true },
    { k: 'barrio',         label: 'Barrio',                requerido: false },
    { k: 'email',          label: 'Correo',                requerido: false },
    { k: 'empresa',        label: 'Empresa',               requerido: false },
    { k: 'tipoServicio',   label: 'Tipo de servicio',      requerido: false },
    { k: 'diaAcordado',    label: 'Día acordado',          requerido: false },
    { k: 'franjaHoraria',  label: 'Franja horaria',        requerido: false },
  ];

  const abrirPreorden = (registro) => {
    if (!registro.clienteId) {
      mostrarAviso('error', 'Este cliente todavía no existe en Control360. Créalo primero en el módulo Clientes y luego genera la orden desde allí.');
      return;
    }
    const dc = registro.datosCierre || {};
    setPreorden({
      registro,
      datos: {
        nombreCompleto: dc.nombreCompleto || '',
        nit:            dc.nit || '',
        celular:        dc.celular || registro.telefono || '',
        direccion:      dc.direccion || '',
        barrio:         dc.barrio || '',
        email:          dc.email || '',
        empresa:        dc.empresa || '',
        tipoServicio:   dc.tipoServicio || '',
        diaAcordado:    dc.diaAcordado || '',
        franjaHoraria:  dc.franjaHoraria || '',
      },
      // Se recuerda qué campos llegaron vacíos de la llamada para resaltarlos
      // aunque el usuario ya los haya diligenciado.
      faltantesOriginales: CAMPOS_PREORDEN
        .filter(c => c.requerido && !(dc[c.k] || (c.k === 'celular' ? registro.telefono : '')))
        .map(c => c.k),
    });
    setDetalle(null);
  };

  const confirmarPreorden = () => {
    if (!preorden) return;
    const d = preorden.datos;
    const faltantes = CAMPOS_PREORDEN.filter(c => c.requerido && !String(d[c.k] || '').trim());
    if (faltantes.length) {
      mostrarAviso('error', `Completa antes de continuar: ${faltantes.map(f => f.label).join(', ')}`);
      return;
    }
    const prefill = {
      id: preorden.registro.clienteId,
      nombre: (d.nombreCompleto || '').toUpperCase(),
      nit: d.nit || '',
      celular: d.celular || '',
      // Se conserva la sede que atendió la llamada — así la orden se factura
      // por la empresa correcta (Sur / Valle) sin que el asesor la elija a mano.
      empresaId: preorden.registro.empresaId || '',
      direccion: d.direccion || '',
      barrio: d.barrio || '',
      email: d.email || '',
      origen: 'lucy',
      origenRef: preorden.registro.id,
    };
    sessionStorage.setItem('c360_orden_prefill', JSON.stringify(prefill));
    setPreorden(null);
    if (onNavegar) onNavegar('ordenes');
  };

  const inp = { width: '100%', padding: '9px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
  const btnAccion = { border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 800, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' };
  // ✅ LUCY-MES-004 / LUCY-PAQUETE-005 — mismo alto que los botones para que la
  // fila de controles se lea pareja en escritorio y se apile bien en móvil.
  const selectControl = {
    border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 12px',
    fontWeight: 700, fontSize: 12, color: '#1a1a2e', background: '#fff',
    cursor: 'pointer', maxWidth: '100%',
  };

  // ─── Estado: Lucy no activada para este tenant ─────────────────────────────
  if (activa === false) {
    return (
      <div style={{ padding: '12px 12px 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32 }}>☎️</div>
          <div style={{ fontWeight: 800, color: '#1a1a2e', marginTop: 8, fontSize: 15 }}>Llamadas IA (Lucy) no está activa</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            Este módulo se activa manualmente por nuestro equipo. Si te interesa automatizar el seguimiento de vencimientos con llamadas, contáctanos.
          </div>
        </div>
      </div>
    );
  }

  const pctConsumo = config?.topeMinutosMes
    ? Math.min(100, Math.round(((config.minutosConsumidosMes || 0) / config.topeMinutosMes) * 100))
    : 0;

  return (
    <div style={{ padding: '12px 12px 80px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header + controles de operación */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>☎️ Llamadas IA — Lucy</h1>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Seguimiento automático de vencimientos por voz</div>
        </div>
        {esAdmin && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* ✅ LUCY-MES-004 / LUCY-PAQUETE-005 — qué mes y de a cuántos.
                Van ANTES del botón: se lee como una frase, "llamar septiembre,
                de a 50", y así nadie lanza a ciegas. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label htmlFor="lucy-mes" style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Mes a llamar</label>
              <select id="lucy-mes" value={mesCorrida} onChange={(e) => setMesCorrida(e.target.value)} disabled={ocupado}
                style={{ ...selectControl, minWidth: 150 }}>
                {mesesSeleccionables().map(m => (
                  <option key={m} value={m}>
                    {mesBonito(m)}{m === mesActualCO() ? ' (actual)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label htmlFor="lucy-paquete" style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Paquete</label>
              <select id="lucy-paquete" value={paquete} onChange={(e) => setPaquete(e.target.value)} disabled={ocupado}
                style={{ ...selectControl, minWidth: 140 }}>
                {OPCIONES_PAQUETE.map(o => (
                  <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
                ))}
              </select>
            </div>

            <button onClick={lanzarAhora} disabled={ocupado}
              style={{ ...btnAccion, background: '#1a1a2e', color: '#fff', opacity: ocupado ? 0.6 : 1, alignSelf: 'flex-end' }}>
              📞 Lanzar ahora
            </button>
            <button onClick={() => setModalProgramar(true)} disabled={ocupado}
              style={{ ...btnAccion, background: '#e0f2fe', color: '#0369a1', alignSelf: 'flex-end' }}>
              🗓 Programar
            </button>
            <button onClick={() => setModalPrueba(true)} disabled={ocupado}
              style={{ ...btnAccion, background: '#fff8e6', color: '#b45309', alignSelf: 'flex-end' }}>
              🧪 Llamada de prueba
            </button>
          </div>
        )}
      </div>

      {/* Aviso de resultado de acciones */}
      {aviso && (
        <div style={{
          background: aviso.tipo === 'ok' ? '#dcfce7' : '#fee2e2',
          color: aviso.tipo === 'ok' ? '#15803d' : '#b91c1c',
          borderRadius: 10, padding: '10px 14px', fontSize: 12.5, fontWeight: 700, marginBottom: 12,
        }}>
          {aviso.texto}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ✅ LUCY-ASINCRONO-001 — Corrida en vivo                             */}
      {/* A quién está llamando Lucy en este momento. Se refresca solo.       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {corrida?.estado === 'en_curso' && (
        <div style={{ background: '#1a1a2e', color: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{
              width: 9, height: 9, borderRadius: '50%', background: '#4ade80',
              display: 'inline-block', animation: 'c360pulse 1.2s infinite',
            }} />
            <span style={{ fontWeight: 800, fontSize: 12.5, letterSpacing: 0.5 }}>LUCY ESTÁ LLAMANDO</span>
            {/* ✅ LUCY-MES-004: qué mes está llamando, a la vista siempre */}
            {corrida.mes && (
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'rgba(255,255,255,0.14)' }}>
                {mesBonito(corrida.mes)}{corrida.paquete ? ` · paquete de ${corrida.paquete}` : ''}
              </span>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#a5b4fc' }}>
              {corrida.lanzadas || 0}{corrida.totalObjetivo ? ` de ${corrida.totalObjetivo}` : ''} llamada(s) lanzada(s)
            </span>
          </div>

          {corrida.llamandoAhora && (
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '9px 12px', fontSize: 13 }}>
              <span style={{ color: '#9ca3af', fontSize: 11 }}>Marcando ahora</span>
              <div style={{ fontWeight: 800, marginTop: 2 }}>
                {telBonito(corrida.llamandoAhora)}
                {corrida.clienteAhora ? <span style={{ fontWeight: 500, color: '#d1d5db' }}> · {corrida.clienteAhora}</span> : null}
                {corrida.tipoAhora && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: corrida.tipoAhora === 'vehicular' ? '#065f46' : '#3730a3' }}>
                    {corrida.tipoAhora === 'vehicular' ? 'guion corto' : 'guion largo'}
                  </span>
                )}
              </div>
            </div>
          )}

          {corrida.totalObjetivo ? (
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 99, height: 6, overflow: 'hidden', marginTop: 10 }}>
              <div style={{ width: `${Math.min(100, Math.round(((corrida.lanzadas || 0) / corrida.totalObjetivo) * 100))}%`, height: '100%', background: '#4ade80', borderRadius: 99, transition: 'width .4s' }} />
            </div>
          ) : null}

          <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 8 }}>
            Lucy llama de a 3 por minuto para respetar el límite de llamadas simultáneas de tu plan. Puedes cerrar esta pantalla, la corrida sigue.
          </div>

          {/* ✅ LUCY-PARADA-001 — Freno de emergencia */}
          {esAdmin && (
            corrida.senalControl ? (
              <div style={{ marginTop: 10, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 8, padding: '8px 12px', fontSize: 11.5, color: '#fde68a', fontWeight: 700 }}>
                ⏳ Deteniendo… Lucy no marcará más números. Las llamadas ya en curso terminan solas.
                {/* ✅ LUCY-PARADA-002: si el backend se reinició, el motor murió
                    y nadie va a leer la señal — el aviso se quedaría aquí para
                    siempre. Este botón cierra el registro huérfano. */}
                <div style={{ fontWeight: 500, marginTop: 6, color: '#fcd34d' }}>
                  ¿Lleva más de 2 minutos así?{' '}
                  <button onClick={() => detenerCorrida('cancelar')} disabled={ocupado}
                    style={{ background: 'transparent', border: '1px solid #fcd34d', color: '#fde68a', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    Cerrar de todos modos
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button onClick={() => detenerCorrida('pausar')} disabled={ocupado}
                  style={{ ...btnAccion, background: '#f59e0b', color: '#1a1a2e', opacity: ocupado ? 0.6 : 1 }}>
                  ⏸ Pausar
                </button>
                <button onClick={() => detenerCorrida('cancelar')} disabled={ocupado}
                  style={{ ...btnAccion, background: '#dc2626', color: '#fff', opacity: ocupado ? 0.6 : 1 }}>
                  ⏹ Detener todo
                </button>
                <span style={{ fontSize: 10.5, color: '#9ca3af', alignSelf: 'center' }}>
                  Pausar guarda los pendientes · Detener todo descarta la cola
                </span>
              </div>
            )
          )}
          <style>{`@keyframes c360pulse{0%,100%{opacity:1}50%{opacity:.25}}`}</style>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ✅ LUCY-PARADA-001 — Corrida pausada, lista para continuar          */}
      {/* "Continuar" retoma la cola guardada. NO es lo mismo que "Lanzar     */}
      {/* ahora": lanzar re-evalúa toda la base y volvería a llamar a quien   */}
      {/* ya recibió llamada, gastando un intento y minutos ya pagados.       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {corrida?.estado === 'pausada' && (
        <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 12.5, color: '#92400e' }}>⏸ CORRIDA PAUSADA</div>
          <div style={{ fontSize: 12.5, color: '#78350f', marginTop: 4 }}>
            {corrida.llamadasLanzadas || 0} llamada(s) alcanzaron a salir ·{' '}
            <strong>{corrida.pendientesCount || 0} pendiente(s)</strong> guardado(s)
          </div>
          {esAdmin && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={reanudarCorrida} disabled={ocupado || !corrida.pendientesCount}
                style={{ ...btnAccion, background: '#16a34a', color: '#fff', opacity: (ocupado || !corrida.pendientesCount) ? 0.6 : 1 }}>
                ▶️ Continuar donde quedó
              </button>
            </div>
          )}
        </div>
      )}

      {/* ✅ LUCY-PARADA-001 — Corrida cancelada por el usuario */}
      {corrida?.estado === 'cancelada' && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 12.5, color: '#b91c1c' }}>⏹ CORRIDA DETENIDA</div>
          <div style={{ fontSize: 12.5, color: '#7f1d1d', marginTop: 4 }}>
            {corrida.llamadasLanzadas || 0} llamada(s) alcanzaron a salir. Los vencimientos que no se llamaron quedan sin gestionar y entran en la próxima corrida.
          </div>
        </div>
      )}

      {/* Resumen de la última corrida terminada — con motivos de omisión */}
      {corrida?.estado === 'terminada' && (
        <div style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 11, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
            Última corrida{corrida.mes ? ` · ${mesBonito(corrida.mes)}` : ''}
          </div>
          <div style={{ fontSize: 13, color: '#1a1a2e', fontWeight: 700 }}>
            {corrida.llamadasLanzadas || 0} llamada(s) lanzada(s) de {corrida.vencimientosEvaluados ?? '—'} vencimiento(s) evaluado(s)
          </div>

          {/* ✅ LUCY-PAQUETE-005: el paquete se llenó — el siguiente continúa
              donde quedó este, porque la cola se ordena por intentos. */}
          {corrida.paquete && (corrida.llamadasLanzadas || 0) >= corrida.paquete && (
            <div style={{ marginTop: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#1e40af' }}>
              Se completó el paquete de {corrida.paquete}. Vuelve a darle <strong>Lanzar ahora</strong> con el mismo mes cuando quieras el siguiente: Lucy continúa por los que todavía no han recibido ninguna llamada.
            </div>
          )}
          {(() => {
            const m = corrida.motivos || {};
            const filas = [
              ['Sin teléfono válido', m.sin_telefono],
              ['Sin sede con dirección', m.sin_sede],
              ['Ya gestionados', m.ya_gestionado],
              ['Intentos agotados', m.intentos_agotados],
              ['Sin ficha de cliente', m.cliente_inexistente],
              ['Rechazadas por el proveedor', m.fallo_proveedor],
              ['Omitidas por tope de minutos', corrida.omitidasPorTope],
            ].filter(([, v]) => v);
            if (!filas.length) return null;
            return (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {filas.map(([label, v]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280' }}>
                    <span>{label}</span><span style={{ fontWeight: 700, color: '#b45309' }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Barra de consumo de minutos (FIX LUCY-ELEVEN-004) */}
      {config?.topeMinutosMes ? (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: '#374151' }}>Minutos del mes</span>
            <span style={{ color: pctConsumo >= 90 ? '#b91c1c' : '#6b7280', fontWeight: 700 }}>
              {config.minutosConsumidosMes || 0} / {config.topeMinutosMes} min
            </span>
          </div>
          <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${pctConsumo}%`, height: '100%', borderRadius: 99, background: pctConsumo >= 90 ? '#ef4444' : pctConsumo >= 70 ? '#f59e0b' : '#22c55e', transition: 'width .3s' }} />
          </div>
        </div>
      ) : null}

      {/* Corridas programadas pendientes */}
      {esAdmin && programadas.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 11, color: '#1d4ed8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
            🗓 Corridas programadas
          </div>
          {programadas.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, padding: '4px 0' }}>
              <span style={{ color: '#1e3a8a', fontWeight: 700 }}>
                {p.fechaHora?.replace('T', ' · ')} (hora Colombia)
              </span>
              <button onClick={() => cancelarProgramada(p.id)}
                style={{ border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tarjetas resumen */}
      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 8, marginBottom: 14 }}>
          <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Total llamadas</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{resumen.total || 0}</div>
          </div>
          <div style={{ background: '#dcfce7', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#15803d' }}>Cierres</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d' }}>{resumen.cerrada || 0}</div>
          </div>
          <div style={{ background: '#e0f2fe', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#0369a1' }}>Reagendadas</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0369a1' }}>{resumen.reagendada || 0}</div>
          </div>
          <div style={{ background: '#fff8e6', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#b45309' }}>Escaladas</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#b45309' }}>{resumen.escalado_asesor || 0}</div>
          </div>
          <div style={{ background: '#f3f4f6', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>No contestó</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#6b7280' }}>{resumen.sin_respuesta || 0}</div>
          </div>
        </div>
      )}

      {/* Filtros por resultado */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => setFiltroResultado('')} style={{ border: `2px solid ${!filtroResultado ? '#1a1a2e' : 'transparent'}`, background: !filtroResultado ? '#1a1a2e' : '#fff', color: !filtroResultado ? '#fff' : '#374151', borderRadius: 99, padding: '5px 14px', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
          Todas
        </button>
        {Object.entries(RESULTADOS).map(([k, v]) => (
          <button key={k} onClick={() => setFiltroResultado(filtroResultado === k ? '' : k)} style={{ border: `2px solid ${filtroResultado === k ? v.color : 'transparent'}`, background: v.bg, color: v.color, borderRadius: 99, padding: '5px 14px', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
            {v.icon} {v.label}
          </button>
        ))}
      </div>

      {/* Lista de llamadas */}
      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Cargando...</div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', border: '1.5px solid #e5e7eb' }}>
          <div style={{ fontSize: 32 }}>☎️</div>
          <div style={{ fontWeight: 700, color: '#1a1a2e', marginTop: 8 }}>Sin llamadas todavía</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Lucy llama automáticamente los primeros días hábiles de cada mes — o usa "Lanzar ahora"</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {listaFiltrada.map(reg => {
            const esCierre = reg.resultado === 'cerrada';
            const r = RESULTADOS[reg.resultado] || (reg.estado === 'en_curso' ? EN_CURSO : RESULTADOS.sin_respuesta);
            return (
              <div key={reg.id} onClick={() => setDetalle(reg)}
                style={{
                  background: esCierre ? '#f0fdf4' : '#fff',
                  border: esCierre ? '2px solid #4ade80' : '1.5px solid #e5e7eb',
                  borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: '#1a1a2e' }}>
                      {reg.esPrueba ? '🧪 Llamada de prueba' : (reg.datosCierre?.nombreCompleto || telBonito(reg.telefono) || 'Sin nombre')}
                    </span>
                    <span style={{ background: r.bg, color: r.color, fontWeight: 800, fontSize: 10, padding: '2px 9px', borderRadius: 8 }}>
                      {r.icon} {r.label}
                    </span>
                    {reg.intento > 1 && (
                      <span style={{ background: '#f3f4f6', color: '#6b7280', fontWeight: 700, fontSize: 10, padding: '2px 8px', borderRadius: 8 }}>
                        Intento {reg.intento}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#6b7280' }}>
                    {telBonito(reg.telefono)}{reg.duracionSegundos ? ` · ${Math.round(reg.duracionSegundos / 60)} min` : ''} · {formatFecha(reg.createdAt)}
                  </div>
                </div>
                {esCierre && (
                  <button onClick={(e) => { e.stopPropagation(); abrirPreorden(reg); }}
                    style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    + Crear orden
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal detalle — incluye transcripción para auditoría del suscriptor */}
      {detalle && (
        <div onClick={() => setDetalle(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 540, maxHeight: '85vh', overflowY: 'auto', padding: '18px 18px 28px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1a1a2e' }}>
                  {detalle.esPrueba ? '🧪 Llamada de prueba' : (detalle.datosCierre?.nombreCompleto || telBonito(detalle.telefono))}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{formatFecha(detalle.createdAt)}</div>
              </div>
              <button onClick={() => setDetalle(null)} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, width: 30, height: 30, cursor: 'pointer' }}>✕</button>
            </div>

            {detalle.datosCierre && (
              <div style={{ background: '#f0fdf4', border: '1.5px solid #4ade80', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 11, color: '#15803d', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  ✅ Datos recolectados por Lucy
                </div>
                {[
                  ['Empresa', detalle.datosCierre.empresa],
                  ['NIT', detalle.datosCierre.nit],
                  ['Dirección', detalle.datosCierre.direccion],
                  ['Barrio', detalle.datosCierre.barrio],
                  ['Celular', telBonito(detalle.datosCierre.celular)],
                  ['Email', detalle.datosCierre.email],
                  ['Tipo de servicio', detalle.datosCierre.tipoServicio],
                  ['Día acordado', detalle.datosCierre.diaAcordado],
                  ['Franja horaria', detalle.datosCierre.franjaHoraria],
                ].filter(([, v]) => v).map(([label, valor]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' }}>
                    <span style={{ color: '#374151' }}>{label}</span>
                    <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{valor}</span>
                  </div>
                ))}
                {!detalle.esPrueba && (
                  <button onClick={() => abrirPreorden(detalle)}
                    style={{ width: '100%', marginTop: 10, background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    📋 Revisar preorden
                  </button>
                )}
              </div>
            )}

            {detalle.resumenIA && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 11, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  🤖 Resumen de la llamada
                </div>
                <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: '#374151', lineHeight: 1.6 }}>
                  {detalle.resumenIA}
                </div>
              </div>
            )}

            {detalle.transcripcion && (
              <div>
                <div style={{ fontWeight: 800, fontSize: 11, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  📝 Transcripción completa
                </div>
                <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {detalle.transcripcion}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* Modal PREORDEN — LUCY-PREORDEN-001                                  */}
      {/* Revisión humana obligatoria de los datos que Lucy tomó por voz.     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {preorden && (
        <div onClick={() => setPreorden(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', padding: '20px 18px 24px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#1a1a2e' }}>📋 Preorden</div>
              <button onClick={() => setPreorden(null)}
                style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, width: 30, height: 30, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 14 }}>
              Lucy tomó estos datos por teléfono. Revísalos y corrige lo que haga falta antes de generar la orden.
            </div>

            {preorden.faltantesOriginales.length > 0 && (
              <div style={{ background: '#fff8e6', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 11.5, color: '#92400e', fontWeight: 600 }}>
                ⚠️ Lucy no logró confirmar {preorden.faltantesOriginales.length} dato(s) obligatorio(s) en la llamada. Están resaltados abajo.
              </div>
            )}

            {CAMPOS_PREORDEN.map(campo => {
              const vacioEnLlamada = preorden.faltantesOriginales.includes(campo.k);
              const vacioAhora = !String(preorden.datos[campo.k] || '').trim();
              const alertar = campo.requerido && (vacioAhora || vacioEnLlamada);
              return (
                <div key={campo.k} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: alertar ? '#b45309' : '#374151', display: 'block', marginBottom: 4 }}>
                    {campo.label}{campo.requerido ? ' *' : ''}{vacioEnLlamada ? ' · no confirmado en la llamada' : ''}
                  </label>
                  <input
                    value={preorden.datos[campo.k] || ''}
                    onChange={e => setPreorden(p => ({ ...p, datos: { ...p.datos, [campo.k]: e.target.value } }))}
                    style={{ ...inp, borderColor: alertar ? '#fcd34d' : '#e5e7eb', background: alertar ? '#fffbeb' : '#fff' }}
                  />
                </div>
              );
            })}

            {preorden.registro.transcripcion && (
              <details style={{ marginTop: 12, marginBottom: 4 }}>
                <summary style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', cursor: 'pointer' }}>
                  📝 Ver transcripción para verificar un dato
                </summary>
                <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
                  {preorden.registro.transcripcion}
                </div>
              </details>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setPreorden(null)} style={{ ...btnAccion, flex: 1, background: '#f3f4f6', color: '#374151' }}>
                Cancelar
              </button>
              <button onClick={confirmarPreorden} style={{ ...btnAccion, flex: 2, background: '#15803d', color: '#fff' }}>
                ✓ Confirmar y crear orden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Programar (FIX LUCY-ELEVEN-004) */}
      {modalProgramar && (
        <div onClick={() => setModalProgramar(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380, padding: '20px 18px' }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>🗓 Programar llamadas</div>
            <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 12 }}>
              Lucy llamará a los clientes con vencimientos pendientes de tu empresa el día y hora que elijas (hora Colombia).
            </div>
            {/* ✅ LUCY-MES-004 / LUCY-PAQUETE-005: se programa lo que está
                elegido arriba. Mostrarlo evita programar un mes sin querer. */}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', marginBottom: 14, fontSize: 12, color: '#374151' }}>
              Se programará: <strong>{mesBonito(mesCorrida)}</strong>{paquete ? <> · paquete de <strong>{paquete}</strong></> : <> · <strong>todas</strong> las pendientes</>}
              <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 2 }}>Para cambiarlo, cierra esta ventana y ajusta el mes o el paquete arriba.</div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Fecha</label>
              <input type="date" value={progFecha} onChange={e => setProgFecha(e.target.value)} style={inp} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Hora</label>
              <input type="time" value={progHora} onChange={e => setProgHora(e.target.value)} style={inp} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setModalProgramar(false)} style={{ ...btnAccion, flex: 1, background: '#f3f4f6', color: '#374151' }}>Cancelar</button>
              <button onClick={programarCorrida} disabled={ocupado} style={{ ...btnAccion, flex: 1, background: '#0369a1', color: '#fff', opacity: ocupado ? 0.6 : 1 }}>Programar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Llamada de prueba (FIX LUCY-ELEVEN-004) */}
      {modalPrueba && (
        <div onClick={() => setModalPrueba(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380, padding: '20px 18px' }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>🧪 Llamada de prueba</div>
            <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 14 }}>
              Lucy llamará al número que indiques con datos de ejemplo. Ideal para validar la voz y el guion antes de llamar clientes reales. No afecta vencimientos ni métricas.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Celular (10 dígitos)</label>
              <input type="tel" value={telPrueba} onChange={e => setTelPrueba(e.target.value)} placeholder="3001234567" style={inp} />
            </div>

            {/* ✅ LUCY-CAPACIDAD-001: probar cada guion por separado.
                El corto (mostrador) es el de extintores pequeños que el cliente
                recoge en la sede; el largo agenda visita de técnico. */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>¿Qué guion quieres probar?</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'vehicular', t: 'Corto · mostrador', s: 'ABC 5 lb · sin cita' },
                  { v: 'empresa',   t: 'Largo · con técnico', s: 'ABC 10 lb · agenda visita' },
                ].map(op => (
                  <button key={op.v} onClick={() => setTipoPrueba(op.v)}
                    style={{
                      flex: 1, textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '9px 11px',
                      border: `2px solid ${tipoPrueba === op.v ? '#b45309' : '#e5e7eb'}`,
                      background: tipoPrueba === op.v ? '#fff8e6' : '#fff',
                    }}>
                    <div style={{ fontWeight: 800, fontSize: 11.5, color: tipoPrueba === op.v ? '#b45309' : '#374151' }}>{op.t}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{op.s}</div>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setModalPrueba(false)} style={{ ...btnAccion, flex: 1, background: '#f3f4f6', color: '#374151' }}>Cancelar</button>
              <button onClick={lanzarPrueba} disabled={ocupado} style={{ ...btnAccion, flex: 1, background: '#b45309', color: '#fff', opacity: ocupado ? 0.6 : 1 }}>📞 Llamarme</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
